/**
 * 인증 Hook
 *
 * Google OAuth 로그인/로그아웃 및 사용자 정보를 관리합니다.
 */

import { useGoogleLogin } from '@react-oauth/google'
import { useState, useEffect } from 'react'
import type { AuthUser, AuthStatus, AuthError } from './types'
import { STORAGE_KEYS } from '@/core/config/constants'
import { env } from '@/core/config/env'
import { logger } from '@/core/logger'

/**
 * 인증 Hook 반환 타입
 */
interface UseAuthReturn {
  user: AuthUser | null
  status: AuthStatus
  error: AuthError | null
  login: () => void
  logout: () => void
  isAuthenticated: boolean
}

/**
 * Google OAuth tokeninfo 엔드포인트로 Access Token 유효성 검증
 *
 * @param token - 검증할 Access Token
 * @returns true: 유효, false: 만료/무효
 * @throws Error: 네트워크 오류
 *
 * @why Google 공식 OAuth 검증 엔드포인트를 사용하여 토큰 유효성을 확인합니다.
 * 가볍고 빠르며(200-500ms), 명확한 응답(유효/만료 구분)을 제공합니다.
 */
async function validateToken(token: string): Promise<boolean> {
  try {
    const response = await fetch(
      `https://oauth2.googleapis.com/tokeninfo?access_token=${token}`
    )

    if (response.ok) {
      // 200 OK - 토큰 유효
      return true
    } else if (response.status === 401 || response.status === 400) {
      // 401/400 - 토큰 만료 또는 무효
      logger.warn('Token validation failed: Token expired or invalid')
      return false
    } else {
      // 기타 에러 - 네트워크 문제로 간주
      throw new Error(`Token validation failed with status ${response.status}`)
    }
  } catch (error) {
    logger.error('Token validation network error:', error)
    throw error
  }
}

/**
 * useAuth Hook
 *
 * Google OAuth 인증을 처리하고 사용자 정보를 제공합니다.
 *
 * @example
 * ```tsx
 * function LoginButton() {
 *   const { login, user, isAuthenticated } = useAuth()
 *
 *   if (isAuthenticated) {
 *     return <div>환영합니다, {user.name}님!</div>
 *   }
 *
 *   return <button onClick={login}>Google 로그인</button>
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [status, setStatus] = useState<AuthStatus>('idle')
  const [error, setError] = useState<AuthError | null>(null)

  // Mock 모드: 자동 로그인된 사용자 제공
  useEffect(() => {
    if (env.USE_MOCK_DATA) {
      const mockUser: AuthUser = {
        id: 'user_mock_1',
        email: 'mock@example.com',
        name: 'Mock User',
      }
      setUser(mockUser)
      setStatus('authenticated')

      // Production 환경에서는 localStorage에 저장하지 않음 (모바일 대응)
      // Development에서만 localStorage에 저장하여 새로고침 시 유지
      if (env.ENV === 'development') {
        localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(mockUser))
      }
    } else {
      // 실제 모드: 로컬 스토리지에서 사용자 정보 복원 + 토큰 검증
      const savedUser = localStorage.getItem(STORAGE_KEYS.USER_INFO)
      const savedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)

      if (savedUser && savedToken) {
        // 토큰이 있는 경우: 유효성 검증 수행
        setStatus('loading') // 검증 중 로딩 표시

        validateToken(savedToken)
          .then((isValid) => {
            if (isValid) {
              // 토큰 유효 - 사용자 정보 복원
              try {
                setUser(JSON.parse(savedUser))
                setStatus('authenticated')
              } catch {
                // JSON 파싱 실패 - 손상된 데이터
                logger.error('Failed to parse saved user info')
                localStorage.removeItem(STORAGE_KEYS.USER_INFO)
                localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
                setStatus('idle')
              }
            } else {
              // 토큰 만료 - localStorage 초기화 및 로그인 화면 표시
              logger.warn('Stored token is expired. Please login again.')
              localStorage.removeItem(STORAGE_KEYS.USER_INFO)
              localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
              setStatus('idle')
            }
          })
          .catch((error) => {
            // 네트워크 에러 - 에러 상태로 설정
            logger.error('Token validation error:', error)
            setError({
              code: 'VALIDATION_ERROR',
              message: '네트워크 연결을 확인해주세요.',
            })
            setStatus('error')
          })
      }
      // else: savedUser나 savedToken이 없으면 status='idle' 유지 (로그인 화면)
    }
  }, [])

  // Google 로그인 (실제 모드에서만 사용)
  // Mock 모드에서는 useGoogleLogin을 호출하지 않음 (Provider가 없으므로)
  const googleLogin = env.USE_MOCK_DATA
    ? () => {}
    : useGoogleLogin({
        onSuccess: async (tokenResponse) => {
          setStatus('loading')
          try {
            // Access Token으로 사용자 정보 가져오기
            const userInfoResponse = await fetch(
              'https://www.googleapis.com/oauth2/v3/userinfo',
              {
                headers: {
                  Authorization: `Bearer ${tokenResponse.access_token}`,
                },
              }
            )

            if (!userInfoResponse.ok) {
              throw new Error('사용자 정보를 가져올 수 없습니다')
            }

            const userInfo = await userInfoResponse.json()

            const authUser: AuthUser = {
              id: userInfo.sub,
              email: userInfo.email,
              name: userInfo.name,
              picture: userInfo.picture,
            }

            // 사용자 정보 저장
            setUser(authUser)
            setStatus('authenticated')
            setError(null)
            localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(authUser))
            localStorage.setItem(
              STORAGE_KEYS.AUTH_TOKEN,
              tokenResponse.access_token
            )
          } catch (err) {
            const authError: AuthError = {
              code: 'LOGIN_FAILED',
              message:
                err instanceof Error ? err.message : '로그인에 실패했습니다',
            }
            setError(authError)
            setStatus('error')
          }
        },
        onError: () => {
          const authError: AuthError = {
            code: 'LOGIN_CANCELLED',
            message: '로그인이 취소되었습니다',
          }
          setError(authError)
          setStatus('error')
        },
        scope: [
          'https://www.googleapis.com/auth/spreadsheets',
          'https://www.googleapis.com/auth/userinfo.email',
          'https://www.googleapis.com/auth/userinfo.profile',
        ].join(' '),
      })

  /**
   * 로그인
   */
  const login = () => {
    if (env.USE_MOCK_DATA) {
      logger.info('🔧 Mock 모드: 로그인이 이미 완료되었습니다')
      return
    }
    googleLogin()
  }

  /**
   * 로그아웃
   */
  const logout = () => {
    setUser(null)
    setStatus('idle')
    setError(null)
    localStorage.removeItem(STORAGE_KEYS.USER_INFO)
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
  }

  return {
    user,
    status,
    error,
    login,
    logout,
    isAuthenticated: status === 'authenticated',
  }
}
